// src/js/utils/feedArcs.ts
// Phase 250 (250-02) — dependency-free join/quit arc matching and git-graph lane assignment
// for the `/mc/feed` Activity Stream timeline connectors (D-01, D-02, D-03, D-04, D-05, D-06,
// D-07, D-13).
//
// This module exists because `src/js/mc/feed.ts` imports `highcharts` and
// `datatables.net-bs5` at module scope, so `node tests/mc/*.test.ts` could never load it
// without a bundler. Every function here is pure (no document, no window, no fetch, no
// setTimeout, no import) so that `tests/mc/feedArcs.test.ts` can pin it with plain `node`,
// no bundler and no jsdom — the same reason `utils/feedTail.ts` was extracted as a zero-import
// module before this one.
//
// ---------------------------------------------------------------------------------------
// 250-RESEARCH.md Open Question 2 (fold into feedTail.ts, or a new file?) — RESOLVED here,
// a stated call rather than a silent choice.
// ---------------------------------------------------------------------------------------
// This logic lives in its OWN file rather than folding into `feedTail.ts`. `feedTail.ts`'s
// declared scope is live-tail watermark/dedup arithmetic (D-06/D-08/D-14 of Phase 243) — it
// has nothing to do with arc matching, and arc matching applies even with live-tail entirely
// absent (a static page-0 load with no polling still needs its connectors drawn). Keeping
// these concerns in separate files also keeps each one's zero-import test file focused on one
// algorithm family.
//
// Session pairing here generalizes the server-side single-player precedent
// `App\Mc\SessionPairing::pair()` (`app/Mc/SessionPairing.php:57-103`) — FIFO-close-on-quit,
// an unmatched join is preserved (never dropped) — from one player's single `$open` variable
// to a `Map`-keyed multi-player, multi-server version. Lane assignment is the standard
// "minimum meeting rooms" / git-graph-column greedy interval-scheduling algorithm.

/** Lanes are UNCAPPED — `assignLanes()` grows a new lane rather than reusing an occupied one,
 *  so overlapping spans never share a lane no matter how many are concurrently open. This
 *  constant is only the FLOOR the gutter reserves visually (`feed.ts` widens the gutter beyond
 *  it, never below it) so the common case of 0-3 concurrent sessions keeps its original spacing. */
export const MIN_LANE_COUNT = 3;

/** UI-SPEC "Timeline Connector / Swimlane Contract" — swimlane track width, 12px per lane. */
export const LANE_TRACK_PX = 12;

/** Gutter floor width, 40px = MIN_LANE_COUNT lanes * 12px + 4px (`--ksat-space-xs`) breathing
 *  room before the badge. The actual rendered gutter grows past this via the `--mc-lane-count`
 *  CSS custom property `feed.ts` sets from the real max concurrent lane count. */
export const MIN_LANE_GUTTER_PX = 40;

/** The narrow structural subset of `feed.ts`'s `StreamRow` this module needs — never an
 *  import of `StreamRow` itself (this file stays zero-import). */
export interface ArcRowLike {
    event_type: string;
    player_uuid: string;
    server_key: string;
}

export type ArcSpanKind = 'arc' | 'join-stub' | 'quit-stub';

export interface ArcSpan {
    kind: ArcSpanKind;
    topIdx: number;
    bottomIdx: number;
    joinIdx: number | null;
    quitIdx: number | null;
}

export type LaneSegmentKind = 'top' | 'bottom' | 'through';

export type LaneEndpoint = 'join' | 'quit' | null;

export interface LaneSegment {
    lane: number;
    kind: LaneSegmentKind;
    endpoint: LaneEndpoint;
    fadeTop: boolean;
    fadeBottom: boolean;
}

/**
 * D-02: composite map key scoping a join/quit pair to the SAME (player, server) pair. The
 * `\u0000` (NUL) separator is deliberate, not decorative: a player uuid and a server slug can
 * both legally contain `-` and `_`, so a printable separator (e.g. `|`) could theoretically let
 * a crafted `server_key` make `(uuidA, "x|y")` collide with `(uuidA + "|x", "y")` and
 * cross-match two different players' sessions — exactly the T-250-05 spoofing threat this
 * function exists to close. `\u0000` can never appear in either field's legitimate values, so
 * no such collision is possible.
 */
export function sessionArcKey(row: ArcRowLike): string {
    return `${row.player_uuid}\u0000${row.server_key}`;
}

/**
 * D-01/D-02/D-04/D-05/D-06/D-07: pairs a `session.join` with the same `(player_uuid,
 * server_key)`'s later `session.quit`, skipping every intervening row of any other type
 * (deaths, kills, other players' rows) — D-01's "skip over" rule and D-07's "the type filter
 * never changes matching logic" rule.
 *
 * Index convention: `rows[0]` is the NEWEST row (top of the DOM), `rows[rows.length - 1]` is
 * the OLDEST (bottom). A join therefore normally has a LARGER index than its own quit.
 *
 * D-05: this function always re-walks the FULL current row set (a page-0 load, a Load More
 * append, or a live-tail prepend) — it holds no state across calls. That full-recompute
 * discipline is what lets two stubs from separate loaded pages resolve into one matched arc
 * once both pages are present in the same call.
 *
 * D-06: no cap of any kind is applied to a span's height, and no second row-count cap is
 * added here — `STREAM_ROW_CAP` in `feedTail.ts` already bounds the caller's row set
 * (250-RESEARCH.md Pitfall 4).
 */
export function matchSessions(rows: ArcRowLike[]): ArcSpan[] {
    const openJoins = new Map<string, number[]>();
    const spans: ArcSpan[] = [];

    // Walk oldest -> newest (D-05: a full, stateless recompute over the whole current set).
    for (let i = rows.length - 1; i >= 0; i--) {
        const row = rows[i];
        if (row.event_type !== 'session.join' && row.event_type !== 'session.quit') {
            // D-01/D-07: skip death/kill/other-player/other-type rows without altering matching.
            continue;
        }
        const key = sessionArcKey(row);

        if (row.event_type === 'session.join') {
            const queue = openJoins.get(key);
            if (queue) {
                queue.push(i);
            } else {
                openJoins.set(key, [i]);
            }
            continue;
        }

        // session.quit — FIFO: claim the OLDEST unclaimed join for this key.
        const queue = openJoins.get(key);
        const joinIdx = queue && queue.length > 0 ? queue.shift() : undefined;
        if (joinIdx !== undefined) {
            // Math.max/Math.min guard against an inverted span: two events sharing the exact
            // same `occurred_at` second can arrive in either array order, and a naive
            // {topIdx: joinIdx, bottomIdx: i} would sometimes render a connector pointing away
            // from itself.
            spans.push({
                kind: 'arc',
                topIdx: Math.min(joinIdx, i),
                bottomIdx: Math.max(joinIdx, i),
                joinIdx,
                quitIdx: i,
            });
        } else {
            // D-04: no join found anywhere in the currently-loaded window — an out-of-view
            // leave stub, extending down to the bottom of the loaded set.
            spans.push({ kind: 'quit-stub', topIdx: i, bottomIdx: rows.length - 1, joinIdx: null, quitIdx: i });
        }
    }

    // D-04: everything still queued after the full walk never got a quit within the loaded
    // window (still online, or genuinely never quit) — an open-join stub extending up to the
    // top of the loaded set.
    for (const queue of openJoins.values()) {
        for (const joinIdx of queue) {
            spans.push({ kind: 'join-stub', topIdx: 0, bottomIdx: joinIdx, joinIdx, quitIdx: null });
        }
    }

    // Determinism is a test requirement, not a nicety.
    spans.sort((a, b) => {
        if (a.topIdx !== b.topIdx) return a.topIdx - b.topIdx;
        if (a.bottomIdx !== b.bottomIdx) return a.bottomIdx - b.bottomIdx;
        return (a.joinIdx ?? -1) - (b.joinIdx ?? -1);
    });

    return spans;
}

/**
 * Standard "minimum meeting rooms" / git-graph-column greedy interval-scheduling algorithm,
 * UNCAPPED: gives overlapping spans distinct lane indices, reuses a lane once its previous
 * occupant's span has ended, and GROWS a new lane on demand when every existing lane is still
 * occupied. Two spans that overlap in time never share a lane, regardless of how many are
 * concurrently open — there is no fallback that reuses an already-occupied lane.
 *
 * Returns lane indices PARALLEL TO THE INPUT ARRAY's order (`lanes[i]` always describes
 * `spans[i]`) — achieved by sorting an index permutation, never by mutating or reordering
 * `spans` itself.
 */
export function assignLanes(spans: ArcSpan[]): number[] {
    const order = spans.map((_, i) => i);
    order.sort((ia, ib) => {
        const a = spans[ia];
        const b = spans[ib];
        if (a.topIdx !== b.topIdx) return a.topIdx - b.topIdx;
        if (a.bottomIdx !== b.bottomIdx) return a.bottomIdx - b.bottomIdx;
        return ia - ib;
    });

    // laneFreeAfter[lane] = the bottomIdx of that lane's current occupant. A lane is free for
    // a span when laneFreeAfter[lane] < span.topIdx. Starts empty and grows by one element every
    // time no existing lane is free — no fixed-size array, no cap.
    const laneFreeAfter: number[] = [];
    const lanes = new Array<number>(spans.length);

    for (const idx of order) {
        const span = spans[idx];
        let lane = laneFreeAfter.findIndex((freeAfter) => freeAfter < span.topIdx);
        if (lane === -1) {
            // Every existing lane is still occupied by a span that hasn't ended yet — grow a
            // new lane rather than reusing one of them (that would make two overlapping spans
            // render on top of each other).
            lane = laneFreeAfter.length;
            laneFreeAfter.push(-1);
        }
        laneFreeAfter[lane] = Math.max(laneFreeAfter[lane], span.bottomIdx);
        lanes[idx] = lane;
    }

    return lanes;
}

/**
 * D-01..D-06/D-13: runs `matchSessions()` then `assignLanes()`, then emits one lane segment
 * per row per span the row falls inside. Returns an array of the same length as `rows`;
 * element `i` is the (possibly empty) list of segments row `i` must render. Returns `[]` for
 * an empty input.
 *
 * No slicing/truncation of a span's row range — D-06 forbids capping arc/stub height, and this
 * function iterates the FULL `topIdx..bottomIdx` range of every span.
 */
export function laneSegmentsForRows(rows: ArcRowLike[]): LaneSegment[][] {
    if (rows.length === 0) {
        return [];
    }

    const spans = matchSessions(rows);
    const lanes = assignLanes(spans);
    const out: LaneSegment[][] = rows.map(() => []);

    for (let s = 0; s < spans.length; s++) {
        const span = spans[s];
        const lane = lanes[s];

        for (let i = span.topIdx; i <= span.bottomIdx; i++) {
            let kind: LaneSegmentKind;

            if (span.kind === 'arc') {
                // Smaller index is 'top', larger is 'bottom', everything strictly between is
                // 'through'.
                if (i === span.topIdx) {
                    kind = 'top';
                } else if (i === span.bottomIdx) {
                    kind = 'bottom';
                } else {
                    kind = 'through';
                }
            } else if (span.kind === 'join-stub') {
                // The joinIdx row is ALWAYS 'bottom' (its line runs up toward the top of the
                // loaded set) — holds even in the degenerate case joinIdx === 0.
                kind = i === span.joinIdx ? 'bottom' : 'through';
            } else {
                // quit-stub — the quitIdx row is ALWAYS 'top' (its line runs down toward the
                // bottom of the loaded set) — holds even in the degenerate case
                // quitIdx === rows.length - 1.
                kind = i === span.quitIdx ? 'top' : 'through';
            }

            // Colour is independent of geometry — this separation keeps D-14's hue mapping
            // correct even when an inverted same-instant span makes a join the TOP endpoint.
            const endpoint: LaneEndpoint = i === span.joinIdx ? 'join' : i === span.quitIdx ? 'quit' : null;

            out[i].push({ lane, kind, endpoint, fadeTop: false, fadeBottom: false });
        }

        // D-04's open-ended affordance, UI-SPEC's fixed 48px terminal fade. A fade is NEVER
        // set on a 'top'/'bottom' segment — those carry the dot, and there is by definition no
        // stub length to fade over when the endpoint row is itself the terminal row. An 'arc'
        // span never fades.
        if (span.kind === 'join-stub' && span.joinIdx !== null && span.joinIdx > 0) {
            const topRowSegments = out[0];
            const seg = topRowSegments[topRowSegments.length - 1];
            if (seg) seg.fadeTop = true;
        }
        if (span.kind === 'quit-stub' && span.quitIdx !== null && span.quitIdx < rows.length - 1) {
            const bottomRowSegments = out[rows.length - 1];
            const seg = bottomRowSegments[bottomRowSegments.length - 1];
            if (seg) seg.fadeBottom = true;
        }
    }

    return out;
}
